import React from 'react';
import { Navigate } from 'react-router-dom';
import { Row, Col, Button, FormControl, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { canAccessPlanningModule } from '../../utils/roleConstants';
import config from '../../../config';
import utils from '../../utils';
import { getApprovedPurchaseOrderMaterialQuantities } from '../../_services/purchase.service';
import {
    applyClearToBuildInventoryCalculations,
    buildPurchaseQuantityMap,
} from './clearToBuildInventory';
import { canCreatePlanningPurchaseOrder } from './planningPurchaseOrderPermissions';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';

const PLANNING_PRICE_REFRESH_FLAG = "refreshPriceFromInventory";

class ClearToBuild extends React.Component {
    headerLabel = (label, tooltip) => (
        <span
            title={tooltip}
            aria-label={`${label}: ${tooltip}`}
            style={{
                cursor: 'help',
                borderBottom: '1px dotted #6c757d',
                lineHeight: '1.4',
                display: 'inline-block',
            }}
        >
            {label}
        </span>
    );

    constructor(props) {
        super(props);
        this.state = {
            fgPartNo: '',
            partDesc: '',
            demandQty: '',
            bomList: [],
            goodParts: null,
            purchase: null,
            onHold: null,
            bookedQty: null,
            bookedFinishedGoodsQty: null,
            uoms: [],
            sources: [],
            loading: false,
            searched: false,
            error: null,
            purchaseOrderDraft: null,
            goodPartsBreakdownModal: null,
            bookedBreakdownModal: null,
        };

        this.columns = [
            {
                name: 'No',
                selector: (row, i) => i + 1,
                cell: (row, i) => i + 1,
                width: '60px',
            },
            {
                name: 'Material',
                selector: row => row.material,
                sortable: true,
                minWidth: '130px',
                grow: 1,
                wrap: true,
            },
            {
                name: 'Material Description',
                selector: row => row.materialDesc,
                sortable: true,
                minWidth: '140px',
                grow: 2,
                wrap: true,
            },
            {
                name: 'Usage',
                selector: row => row.usage,
                sortable: true,
                width: '90px',
                right: true,
            },
            {
                name: 'UoM',
                selector: row => row.uom,
                sortable: true,
                width: '75px',
            },
            {
                name: 'Where Use',
                selector: row => row.whereUse,
                sortable: true,
                width: '140px',
            },
            {
                name: 'Source',
                selector: row => row.source,
                sortable: true,
                width: '90px',
            },
            {
                name: 'Supplier',
                selector: row => row.supplier,
                sortable: true,
                width: '100px',
            },
            {
                name: this.headerLabel('On-hand Inventory', 'Live usable material stock from Inventory Balance. Receiving and confirmed RM transfers are shown in the breakdown.'),
                selector: row => row.goodParts,
                sortable: true,
                width: '150px',
                right: true,
                cell: row => this.renderGoodPartsCell(row),
            },
            {
                name: this.headerLabel('Purchase', 'Total material quantity in approved Purchase Orders.'),
                selector: row => row.purchase,
                sortable: true,
                width: '110px',
                right: true,
            },
            {
                name: this.headerLabel('On Hold', 'Material quantity not available for use. Calculation: sum of receiving quantity with "On Hold" status for this material.'),
                selector: row => row.onHold,
                sortable: true,
                width: '110px',
                right: true,
            },
            {
                name: this.headerLabel('Booked Qty', 'Material reserved by open JO balance (Planned Output - Actual Output) plus approved sales orders.'),
                selector: row => row.bookedQty,
                sortable: true,
                width: '115px',
                right: true,
                cell: row => this.renderBookedQtyCell(row),
            },
            {
                name: this.headerLabel('Net Inventory', 'Inventory remaining before this CTB demand. Calculation: On-hand Inventory + Purchase - On Hold - Booked Qty.'),
                selector: row => row.totalGoodInventory,
                sortable: true,
                width: '130px',
                right: true,
            },
            {
                name: this.headerLabel('Qty Needed (Demand)', 'Material required for the demand entered above. Calculation: Demand x BOM Usage.'),
                selector: row => row.qtyNeeded,
                sortable: true,
                width: '145px',
                right: true,
            },
            {
                name: this.headerLabel('Demand', 'Finished goods demand quantity entered in the search form.'),
                selector: row => row.demand,
                sortable: true,
                width: '100px',
                right: true,
            },
            {
                name: this.headerLabel('Available Quantity', 'Final available material after this demand. Calculation: Net Inventory - Qty Needed (Demand).'),
                selector: row => row.availableQty,
                sortable: true,
                width: '148px',
                cell: row => {
                    const qty = row.availableQty;
                    if (qty == null) return '—';
                    const color = qty > 0 ? '#28a745' : qty < 0 ? '#dc3545' : '#6c757d';
                    return (
                        <span style={{ color, fontWeight: '600' }}>
                            {qty.toLocaleString()}
                        </span>
                    );
                },
            },
        ];

        this.summaryColumns = [
            {
                name: 'Material',
                selector: row => row.material,
                sortable: true,
                width: '140px',
            },
            {
                name: 'Material Description',
                selector: row => row.materialDesc,
                sortable: true,
                width: '200px',
            },
            {
                name: 'Available Quantity',
                selector: row => row.availableQty,
                sortable: true,
                width: '140px',
                right: true,
                cell: row => (
                    <div style={{ 
                        backgroundColor: '#dc3545', 
                        color: '#fff', 
                        fontWeight: '700', 
                        padding: '2px 10px', 
                        borderRadius: '4px',
                        minWidth: '60px',
                        textAlign: 'center'
                    }}>
                        {row.availableQty.toLocaleString()}
                    </div>
                ),
            },
            {
                name: 'Supplier',
                selector: row => row.supplier,
                sortable: true,
                width: '150px',
            },
            {
                name: 'Action',
                width: '130px',
                cell: row => (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={!this.canCreatePurchaseOrder()}
                        title={this.canCreatePurchaseOrder() ? 'Create Purchase Order' : 'Accounting role required'}
                        onClick={() => this.handleCreatePurchaseOrder(row)}
                    >
                        Create PO
                    </Button>
                ),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            },
        ];
    }

    getCurrentUser = () => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch (e) {
            return null;
        }
    };

    canCreatePurchaseOrder = () => canCreatePlanningPurchaseOrder(this.getCurrentUser());

    componentDidMount() {
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        },
        fetchJson = (url) =>
            fetch(url, requestOptions)
                .then(r => r.text())
                .then(text => (text ? JSON.parse(text) : []));

        Promise.all([
            fetchJson(`${config.apiUrl}/getAllMaterialUom`),
            fetchJson(`${config.apiUrl}/getAllMaterialSource`)
        ]).then(([uoms, sources]) => {
            this.setState({ uoms, sources });
        });
    }

    componentWillUnmount() {
        clearTimeout(this._descDebounceTimer);
    }

    fetchPartDesc = (partNo) => {
        const user = JSON.parse(localStorage.getItem('user'));

        if (!partNo || !partNo.trim() || !user || !user.accountId) return;
        fetch(`${config.apiUrl}/getFGByPartNo?partNo=${encodeURIComponent(partNo.trim())}&company=${user.accountId}`)
            .then(r => r.json())
            .then(data => {
                const desc = data?.partDesc || data?.description || data?.partDescription || '';
                this.setState({ partDesc: desc });
            })
            .catch(() => this.setState({ partDesc: '' }));
    };

    handleSearch = () => {
        const { fgPartNo, demandQty } = this.state;
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.accountId) {
            alert('Please login to continue.');
            return;
        }
        if (!fgPartNo.trim()) {
            alert('Please enter an FG Part No.');
            return;
        }
        const qty = parseFloat(demandQty);
        if (!demandQty || isNaN(qty) || qty <= 0) {
            alert('Please enter a valid Demand Quantity.');
            return;
        }
        this.setState({ loading: true, error: null, searched: false, bomList: [], goodParts: null, purchase: null, onHold: null, bookedQty: null, bookedFinishedGoodsQty: null });

        Promise.all([
            fetch(`${config.apiUrl}/getClearToBuild?partNo=${encodeURIComponent(fgPartNo.trim())}&company=${user.accountId}`)
                .then(r => {
                    if (!r.ok) throw new Error(`Server returned ${r.status}`);
                    return r.json();
                }),
            getApprovedPurchaseOrderMaterialQuantities()
                .catch(err => {
                    console.error('CTB purchase quantity fetch error:', err);
                    return { data: [] };
                }),
        ])
            .then(([data, purchaseQuantityResponse]) => {
                const purchaseRows = purchaseQuantityResponse?.data || purchaseQuantityResponse || [];
                const purchaseQuantityMap = buildPurchaseQuantityMap(purchaseRows);
                const bomList = (data.bomList || []).map(item => {
                    const calculatedItem = applyClearToBuildInventoryCalculations({
                        item,
                        purchaseQuantityMap,
                        demandQty: qty,
                    });
                    return {
                        ...calculatedItem,
                        uom: utils.mapTo(this.state.uoms, item.uom),
                        source: utils.mapTo(this.state.sources, item.source),
                    };
                });

                // Aggregate totals for summary badges
                const totalGoodParts = bomList.reduce((s, r) => s + (r.goodParts || 0), 0);
                const totalPurchase = bomList.reduce((s, r) => s + (r.purchase || 0), 0);
                const totalOnHold = bomList.reduce((s, r) => s + (r.onHold || 0), 0);
                const totalBookedQty = bomList.reduce((s, r) => s + (r.bookedQty || 0), 0);

                this.setState({
                    bomList,
                    goodParts: totalGoodParts,
                    purchase: totalPurchase,
                    onHold: totalOnHold,
                    bookedQty: totalBookedQty,
                    bookedFinishedGoodsQty: data.bookedFinishedGoodsQty ?? 0,
                    loading: false,
                    searched: true
                });
            })
            .catch(err => {
                console.error('CTB fetch error:', err);
                this.setState({ loading: false, searched: true, error: err.message || 'Failed to fetch data.' });
            });
    };

    getRowUnitPrice = (row) => {
        const price = row?.unitPrice ?? row?.price ?? row?.purchasePrice ?? row?.lastPurchasePrice ?? row?.cost ?? row?.standardCost ?? 0;
        const numberPrice = Number(price);
        return Number.isFinite(numberPrice) ? numberPrice : 0;
    };

    handleCreatePurchaseOrder = (row) => {
        if (!this.canCreatePurchaseOrder()) {
            alert('Accounting role is required to create a Purchase Order.');
            return;
        }

        const shortageQty = Math.abs(Number(row.availableQty) || 0);
        const deliveryDate = moment().format('YYYY-MM-DD');
        const draft = {
            date: deliveryDate,
            deliveryDate,
            supplier: row.supplier || '',
            shippingAddress: '',
            currency: row.currency || 'MYR',
            status: 'Draft',
            notes: [
                `Created from CTB negative variance for FG ${this.state.fgPartNo || '-'}.`,
                `Demand Qty: ${this.state.demandQty || 0}.`,
                `Available Qty: ${row.availableQty ?? 0}.`,
            ].join('\n'),
            items: [{
                itemCode: row.material || '',
                description: row.materialDesc || '',
                unit: row.uom || '',
                qty: shortageQty || 1,
                unitPrice: this.getRowUnitPrice(row),
                [PLANNING_PRICE_REFRESH_FLAG]: true,
                selected: true,
                restock: false,
            }],
        };

        try {
            sessionStorage.setItem('ctbPurchaseOrderDraft', JSON.stringify(draft));
        } catch (e) {
            // Navigation state below is still enough for normal in-app routing.
        }

        this.setState({ purchaseOrderDraft: draft });
    };

    formatSummaryNumber = (value) => {
        return Number(value || 0).toLocaleString();
    };

    formatDetailNumber = (value) => {
        const numberValue = Number(value || 0);
        return Number.isFinite(numberValue)
            ? numberValue.toLocaleString(undefined, { maximumFractionDigits: 4 })
            : value;
    };

    renderBookedQtyCell = (row) => {
        const bookedQty = Number(row.bookedQty || 0);
        const breakdownCount = (row.bookedBreakdown || []).length;
        if (bookedQty === 0 && breakdownCount === 0) return '0';

        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ bookedBreakdownModal: row });
                }}
                title={breakdownCount > 0 ? 'View JO and SO breakdown' : 'No breakdown available'}
                style={{
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    textDecoration: 'underline',
                    fontWeight: 600,
                    cursor: 'pointer',
                }}
            >
                {this.formatDetailNumber(bookedQty)}
            </button>
        );
    };

    renderGoodPartsCell = (row) => {
        const goodParts = Number(row.goodParts || 0);
        const breakdownCount = (row.goodPartsBreakdown || []).length;
        if (goodParts === 0 && breakdownCount === 0) return '0';

        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ goodPartsBreakdownModal: row });
                }}
                title={breakdownCount > 0 ? 'View inventory movement breakdown' : 'No breakdown available'}
                style={{
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    textDecoration: 'underline',
                    fontWeight: 600,
                    cursor: 'pointer',
                }}
            >
                {this.formatDetailNumber(goodParts)}
            </button>
        );
    };

    formatBreakdownDate = (value) => {
        if (!value) return '-';
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : value;
    };

    formatMovementLocation = (item) => {
        if (item.sourceType === 'BALANCE') {
            return item.area ? `${item.locationCode || '-'} (${item.area})` : (item.locationCode || '-');
        }
        const from = item.fromLocationCode || '-';
        const to = item.toLocationCode || item.locationCode || '-';
        return `${from} -> ${to}`;
    };

    renderMovementQuantity = (item) => {
        const rawValue = item.signedQty ?? item.quantity ?? 0;
        const qty = Number(rawValue || 0);
        const color = qty < 0 ? '#dc3545' : qty > 0 ? '#198754' : '#495057';
        const prefix = qty > 0 && item.direction !== 'BALANCE' ? '+' : '';

        return (
            <span style={{ color, fontWeight: 700 }}>
                {prefix}{this.formatDetailNumber(qty)}
            </span>
        );
    };

    renderBreakdownSummaryCard = ({ label, value, helper, color }) => {
        const accent = color || '#212529';
        const helperId = `good-parts-breakdown-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

        return (
            <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '150px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>
                        {label}
                    </div>
                    <OverlayTrigger
                        trigger="click"
                        placement="top"
                        rootClose
                        overlay={(
                            <Popover id={helperId}>
                                <Popover.Body>{helper}</Popover.Body>
                            </Popover>
                        )}
                    >
                        <button
                            type="button"
                            aria-label={`${label} info`}
                            title={`${label} info`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                border: `1px solid ${accent}`,
                                background: '#fff',
                                color: accent,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                lineHeight: 1,
                                padding: 0,
                                cursor: 'pointer',
                                flex: '0 0 auto',
                            }}
                        >
                            i
                        </button>
                    </OverlayTrigger>
                </div>
                <div style={{ marginTop: '6px', fontSize: '18px', fontWeight: 800, color: accent }}>
                    {value}
                </div>
            </div>
        );
    };

    renderGoodPartsBreakdownModal = () => {
        const row = this.state.goodPartsBreakdownModal;
        if (!row) return null;

        const rows = row.goodPartsBreakdown || [];
        const movementRows = rows.filter(item => item.sourceType !== 'BALANCE');
        const receivingTotal = movementRows
            .filter(item => item.sourceType === 'RECEIVING')
            .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        const transferOutTotal = movementRows
            .filter(item => item.sourceType === 'TRANSFER' && Number(item.signedQty || 0) < 0)
            .reduce((sum, item) => sum + Math.abs(Number(item.signedQty) || 0), 0);
        const transferInTotal = movementRows
            .filter(item => item.sourceType === 'TRANSFER' && Number(item.signedQty || 0) > 0)
            .reduce((sum, item) => sum + (Number(item.signedQty) || 0), 0);
        const total = Number(row.goodParts || 0);
        const movementNet = receivingTotal + transferInTotal - transferOutTotal;
        const balanceDifference = total - movementNet;

        return (
            <Modal
                show
                onHide={() => this.setState({ goodPartsBreakdownModal: null })}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>On-hand Inventory Movement Breakdown</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: '12px', color: '#495057' }}>
                        <strong>{row.material}</strong>
                        {row.materialDesc ? ` - ${row.materialDesc}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                        {this.renderBreakdownSummaryCard({
                            label: 'LIVE BALANCE USED',
                            value: this.formatDetailNumber(total),
                            helper: 'The current usable stock CTB uses for On-hand Inventory. This comes from Inventory Balance for RM Warehouse locations.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'MOVEMENT NET',
                            value: this.formatDetailNumber(movementNet),
                            helper: 'Receiving plus Transfer In minus Transfer Out from the movement rows shown in this modal.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'BALANCE DIFFERENCE',
                            value: `${balanceDifference > 0 ? '+' : ''}${this.formatDetailNumber(balanceDifference)}`,
                            color: balanceDifference === 0 ? '#212529' : '#b58100',
                            helper: 'Live Balance Used minus Movement Net. A non-zero value means the live balance includes older/manual adjustments or movements not fully represented by the displayed history.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'RECEIVED',
                            value: this.formatDetailNumber(receivingTotal),
                            color: '#198754',
                            helper: 'Total accepted receiving quantity for this material in the movement history.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'TRANSFER IN',
                            value: this.formatDetailNumber(transferInTotal),
                            color: '#198754',
                            helper: 'Confirmed RM transfer quantity that moved this material back into usable RM Warehouse stock.',
                        })}
                        {this.renderBreakdownSummaryCard({
                            label: 'TRANSFER OUT',
                            value: this.formatDetailNumber(transferOutTotal),
                            color: '#dc3545',
                            helper: 'Confirmed RM transfer quantity that moved this material out of usable RM Warehouse stock, usually to Production or Scrap.',
                        })}
                    </div>
                    <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                        <table className="table table-sm table-hover" style={{ marginBottom: 0 }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                <tr>
                                    <th>Date</th>
                                    <th>Movement</th>
                                    <th>Reference</th>
                                    <th>Location Flow</th>
                                    <th>Status</th>
                                    <th>Lot No</th>
                                    <th style={{ textAlign: 'right' }}>Quantity</th>
                                    <th>Created By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', color: '#6c757d', padding: '18px' }}>
                                            No inventory movement breakdown available.
                                        </td>
                                    </tr>
                                ) : rows.map((item, index) => (
                                    <tr key={`${item.id || item.transactionId || item.grnNo || index}-${index}`}>
                                        <td>{this.formatBreakdownDate(item.movementDate || item.receivedAt)}</td>
                                        <td>{item.movementType || item.sourceType || '-'}</td>
                                        <td>{item.reference || item.transactionId || item.grnNo || item.invoiceNo || '-'}</td>
                                        <td>{this.formatMovementLocation(item)}</td>
                                        <td>{item.status || item.area || '-'}</td>
                                        <td>{item.lotNo || item.supplierMfgLotNo || '-'}</td>
                                        <td style={{ textAlign: 'right' }}>{this.renderMovementQuantity(item)}</td>
                                        <td>{item.createdBy || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => this.setState({ goodPartsBreakdownModal: null })}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    renderBookedBreakdownModal = () => {
        const row = this.state.bookedBreakdownModal;
        if (!row) return null;

        const rows = row.bookedBreakdown || [];
        const joRows = rows.filter(item => item.sourceType === 'JO');
        const soRows = rows.filter(item => item.sourceType === 'SO');
        const total = Number(row.bookedQty || 0);

        return (
            <Modal
                show
                onHide={() => this.setState({ bookedBreakdownModal: null })}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Booked Qty Breakdown</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: '12px', color: '#495057' }}>
                        <strong>{row.material}</strong>
                        {row.materialDesc ? ` - ${row.materialDesc}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                        <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '130px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>TOTAL BOOKED</div>
                            <div style={{ fontSize: '18px', fontWeight: 800 }}>{this.formatDetailNumber(total)}</div>
                        </div>
                        <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '130px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>OPEN JO</div>
                            <div style={{ fontSize: '18px', fontWeight: 800 }}>{this.formatDetailNumber(joRows.reduce((sum, item) => sum + (Number(item.bookedQty) || 0), 0))}</div>
                        </div>
                        <div style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '130px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d' }}>APPROVED SO</div>
                            <div style={{ fontSize: '18px', fontWeight: 800 }}>{this.formatDetailNumber(soRows.reduce((sum, item) => sum + (Number(item.bookedQty) || 0), 0))}</div>
                        </div>
                    </div>
                    <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                        <table className="table table-sm table-hover" style={{ marginBottom: 0 }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                <tr>
                                    <th>Source</th>
                                    <th>Reference</th>
                                    <th>FG Part No</th>
                                    <th>FG Description</th>
                                    <th style={{ textAlign: 'right' }}>Planned Output</th>
                                    <th style={{ textAlign: 'right' }}>Actual Output</th>
                                    <th style={{ textAlign: 'right' }}>Usage</th>
                                    <th style={{ textAlign: 'right' }}>Booked Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', color: '#6c757d', padding: '18px' }}>
                                            No breakdown available.
                                        </td>
                                    </tr>
                                ) : rows.map((item, index) => (
                                    <tr key={`${item.sourceType}-${item.jobNo || item.soId || index}-${index}`}>
                                        <td>{item.sourceType === 'SO' ? 'Approved SO' : 'Open JO'}</td>
                                        <td>{item.sourceType === 'SO' ? (item.soId || '-') : (item.jobNo || '-')}</td>
                                        <td>{item.fgPartNo || '-'}</td>
                                        <td>{item.fgPartDesc || '-'}</td>
                                        <td style={{ textAlign: 'right' }}>{item.sourceType === 'SO' ? this.formatDetailNumber(item.salesOrderQty) : this.formatDetailNumber(item.plannedQty)}</td>
                                        <td style={{ textAlign: 'right' }}>{item.sourceType === 'SO' ? '-' : this.formatDetailNumber(item.actualQty)}</td>
                                        <td style={{ textAlign: 'right' }}>{this.formatDetailNumber(item.usage)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{this.formatDetailNumber(item.bookedQty)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => this.setState({ bookedBreakdownModal: null })}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    calculateCtbResult = (bomList) => {
        const buildableQuantities = (bomList || [])
            .map(row => {
                const usage = Number(row.usage);
                const netInventory = Number(row.totalGoodInventory);
                if (!Number.isFinite(usage) || usage <= 0 || !Number.isFinite(netInventory)) return null;
                return Math.max(0, Math.floor(netInventory / usage));
            })
            .filter(value => value !== null);

        if (buildableQuantities.length === 0) return 0;
        return Math.min(...buildableQuantities);
    };

    renderKpiTile = ({ label, value, tone, helper }) => {
        const tones = {
            blue: { border: '#b6d4fe', bg: '#f1f7ff', accent: '#0d6efd' },
            cyan: { border: '#9eeaf9', bg: '#effcff', accent: '#087990' },
            green: { border: '#a3cfbb', bg: '#f0fbf5', accent: '#198754' },
            amber: { border: '#ffe69c', bg: '#fff9e8', accent: '#b58100' },
            red: { border: '#f1aeb5', bg: '#fff5f6', accent: '#dc3545' },
            dark: { border: '#ced4da', bg: '#f8f9fa', accent: '#343a40' },
            slate: { border: '#cfd4da', bg: '#f6f8fa', accent: '#495057' },
        };
        const color = tones[tone] || tones.slate;

        return (
            <div
                key={label}
                style={{
                    minWidth: 0,
                    padding: '10px 12px',
                    border: `1px solid ${color.border}`,
                    borderLeft: `4px solid ${color.accent}`,
                    borderRadius: '6px',
                    backgroundColor: color.bg,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase', minWidth: 0 }}>
                        {label}
                    </div>
                    {helper && (
                        <OverlayTrigger
                            trigger="click"
                            placement="top"
                            rootClose
                            overlay={(
                                <Popover id={`ctb-kpi-helper-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                                    <Popover.Body>{helper}</Popover.Body>
                                </Popover>
                            )}
                        >
                            <button
                                type="button"
                                aria-label={`${label} info`}
                                title={`${label} info`}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    border: `1px solid ${color.accent}`,
                                    background: '#fff',
                                    color: color.accent,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    lineHeight: 1,
                                    padding: 0,
                                    cursor: 'pointer',
                                    flex: '0 0 auto',
                                }}
                            >
                                i
                            </button>
                        </OverlayTrigger>
                    )}
                </div>
                <div style={{ marginTop: '4px', fontSize: '15px', fontWeight: 800, lineHeight: 1.1, color: color.accent }}>
                    {this.formatSummaryNumber(value)}
                </div>
            </div>
        );
    };

    renderDemandOverview = () => {
        const {
            fgPartNo, partDesc, demandQty,
            bomList, goodParts, purchase, onHold, bookedFinishedGoodsQty,
            loading, searched,
        } = this.state;
        const shortageCount = (bomList || []).filter(row => row.availableQty < 0).length;
        const ctbResult = this.calculateCtbResult(bomList);
        const metrics = [
            { label: 'Demand Qty', value: demandQty || 0, tone: 'blue', helper: 'Entered build demand' },
            { label: 'Materials', value: bomList.length, tone: 'cyan', helper: 'Required BOM lines' },
            { label: 'Shortages', value: shortageCount, tone: 'red', helper: 'Negative variance' },
            { label: 'On-hand Inventory', value: goodParts || 0, tone: 'green', helper: 'Live usable stock from inventory balance' },
            { label: 'Purchase', value: purchase || 0, tone: 'blue', helper: 'Approved purchase order quantity' },
            { label: 'On Hold', value: onHold || 0, tone: 'amber', helper: 'Unavailable stock' },
            { label: 'Booked FG Units/pcs', value: bookedFinishedGoodsQty || 0, tone: 'cyan', helper: 'Open JO balance + approved SO' },
            { label: 'CTB Result', value: ctbResult, tone: 'dark', helper: 'FG units buildable from net inventory' },
        ];

        return (
            <div
                style={{
                    marginBottom: '20px',
                    marginTop: '20px',
                    padding: '16px',
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
                }}
            >
                <Row className="align-items-end" style={{ rowGap: '10px' }}>
                    <Col sm={12} md={3}>
                        <label style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                            FG Part No
                        </label>
                        <FormControl
                            type="text"
                            id="ctb-fg-part-no"
                            placeholder="e.g. FG-100322"
                            value={fgPartNo}
                            onChange={e => {
                                const val = e.target.value;
                                this.setState({ fgPartNo: val, partDesc: '' });
                                clearTimeout(this._descDebounceTimer);
                                this._descDebounceTimer = setTimeout(() => {
                                    this.fetchPartDesc(val);
                                }, 600);
                            }}
                        />
                    </Col>
                    <Col sm={12} md={5}>
                        <label style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                            Part Description
                        </label>
                        <FormControl
                            type="text"
                            id="ctb-part-desc"
                            placeholder="Auto-fetched from system"
                            value={partDesc}
                            readOnly
                        />
                    </Col>
                    <Col sm={12} md={2}>
                        <label style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                            Demand Quantity
                        </label>
                        <FormControl
                            type="number"
                            id="ctb-demand-qty"
                            min="1"
                            placeholder="e.g. 1000"
                            value={demandQty}
                            onChange={e => this.setState({ demandQty: e.target.value })}
                            onKeyDown={e => { if (e.key === 'Enter') this.handleSearch(); }}
                        />
                    </Col>
                    <Col sm={12} md={2}>
                        <Button
                            variant="primary"
                            id="ctb-search-btn"
                            onClick={this.handleSearch}
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : 'Search'}
                        </Button>
                    </Col>
                </Row>

                {searched && !loading && goodParts != null && (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '10px',
                            marginTop: '14px',
                        }}
                    >
                        {metrics.map(metric => this.renderKpiTile(metric))}
                    </div>
                )}
            </div>
        );
    };

    render() {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return <Navigate to="/login" replace />;
            const user = JSON.parse(userData);
            if (!canAccessPlanningModule(user)) return <Navigate to="/" replace />;
        } catch (e) {
            return <Navigate to="/login" replace />;
        }

        if (this.state.purchaseOrderDraft) {
            return (
                <Navigate
                    to="/purchases/purchase-orders/add"
                    state={{ purchaseOrderDraft: this.state.purchaseOrderDraft }}
                />
            );
        }

        const {
            fgPartNo, partDesc, demandQty,
            bomList, goodParts, purchase, onHold, bookedQty,
            loading, searched, error,
        } = this.state;

        return (
            <div className="container">
                <div style={{ marginTop: '20px' }}>
                    <h4>Clear to Build (CTB) Summary</h4>
                    <p className="text-muted">Enter the FG Part No and Demand Quantity to check material availability</p>

                    {/* ── Filter bar ── */}
                    {this.renderDemandOverview()}

                    {error && (
                        <div style={{ marginBottom: '20px', padding: '10px 12px', border: '1px solid #f1aeb5', borderRadius: '6px', backgroundColor: '#fff5f6', color: '#b02a37', fontSize: '14px' }}>
                            {error}
                        </div>
                    )}

                    {false && (<React.Fragment>
                    <div style={{ marginBottom: '20px', marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        {/* Summary badges — shown after a successful search */}
                        {/* Error message */}
                        {error && (
                            <Row style={{ marginTop: '10px' }}>
                                <Col>
                                    <span style={{ color: '#dc3545', fontSize: '14px' }}>⚠️ {error}</span>
                                </Col>
                            </Row>
                        )}
                    </div>

                    {/* ── BOM Table ── */}
                    </React.Fragment>)}

                    {searched && bomList.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={() => utils.tableToExcel('bom-export-table', 'BOM Details', 'BOM_Details_' + moment().format('YYYYMMDD') + '.xls')}
                            >
                                Export BOM
                            </Button>
                        </div>
                    )}
                    <div id="bom-datatable">
                        <DataTable
                            columns={this.columns}
                            data={bomList}
                            progressPending={loading}
                            progressComponent={<DatacoLoadingIndicator loading={loading} size="compact" label="Loading clear-to-build data" />}
                            pagination
                            paginationPerPage={30}
                            paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                            highlightOnHover
                            striped
                            responsive
                            noDataComponent={
                                searched
                                    ? `No BOM records found for "${fgPartNo}".`
                                    : 'Enter an FG Part No and Demand Quantity above, then click Search.'
                            }
                        />
                    </div>

                    {/* ── CTB Summary Report (negative variance only) ── */}
                    {(() => {
                        const negativeRows = bomList.filter(r => r.availableQty < 0);
                        if (!searched || loading || negativeRows.length === 0) return null;

                        return (
                            <div style={{ marginTop: '40px', marginBottom: '40px', width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h5 style={{ fontWeight: '700', color: '#333', margin: 0 }}>CTB Summary Report (Negative Variance)</h5>
                                    <Button 
                                        variant="primary" 
                                        size="sm"
                                        onClick={() => utils.tableToExcel('ctb-summary-export-table', 'CTB Summary', 'CTB_Summary_' + moment().format('YYYYMMDD') + '.xls')}
                                    >
                                        Export Summary
                                    </Button>
                                </div>
                                <div id="ctb-summary-datatable">
                                    <DataTable
                                        columns={this.summaryColumns}
                                        data={negativeRows}
                                        highlightOnHover
                                        striped
                                        responsive
                                        noDataComponent="No negative variance materials found."
                                    />
                                </div>
                            </div>
                        );
                    })()}

                    {/* Hidden export tables for Excel */}
                    <div id="bom-export-table" style={{ display: 'none' }}>
                        <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th>No</th>
                                    <th>Material</th>
                                    <th>Material Description</th>
                                    <th>Usage</th>
                                    <th>UoM</th>
                                    <th>Where Use</th>
                                    <th>Source</th>
                                    <th>Supplier</th>
                                    <th>On-hand Inventory</th>
                                    <th>Purchase</th>
                                    <th>On Hold</th>
                                    <th>Booked Qty</th>
                                    <th>Net Inventory</th>
                                    <th>Qty Needed (Demand)</th>
                                    <th>Demand</th>
                                    <th>Available Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(bomList || []).map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td>{index + 1}</td>
                                        <td>{item.material || ''}</td>
                                        <td>{item.materialDesc || ''}</td>
                                        <td>{item.usage || 0}</td>
                                        <td>{item.uom || ''}</td>
                                        <td>{item.whereUse || ''}</td>
                                        <td>{item.source || ''}</td>
                                        <td>{item.supplier || ''}</td>
                                        <td>{item.goodParts || 0}</td>
                                        <td>{item.purchase || 0}</td>
                                        <td>{item.onHold || 0}</td>
                                        <td>{item.bookedQty || 0}</td>
                                        <td>{item.totalGoodInventory || 0}</td>
                                        <td>{item.qtyNeeded || 0}</td>
                                        <td>{item.demand || 0}</td>
                                        <td>{item.availableQty || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div id="ctb-summary-export-table" style={{ display: 'none' }}>
                        <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th>Material</th>
                                    <th>Material Description</th>
                                    <th>Available Quantity</th>
                                    <th>Supplier</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const negativeRows = bomList.filter(r => r.availableQty < 0);
                                    return negativeRows.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{item.material || ''}</td>
                                            <td>{item.materialDesc || ''}</td>
                                            <td>{item.availableQty || 0}</td>
                                            <td>{item.supplier || ''}</td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </div>

                    {this.renderGoodPartsBreakdownModal()}
                    {this.renderBookedBreakdownModal()}
                </div>
            </div>
        );
    }
}

export { ClearToBuild };
